import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  input,
} from '@angular/core';

/**
 * Thin wrapper around `@antv/g2plot`. The library is already a
 * dependency of this workspace (see root `package.json`
 * `"@antv/g2plot": "^2.4.33"`), and we import the plot constructors
 * lazily so the drawer bundle doesn't pay the ~200kb cost on app boot.
 *
 * Angular signal inputs drive re-rendering: when either `data` or
 * `config` changes, the underlying plot is disposed and recreated.
 * That is intentionally simple — the drawer panels re-render rarely
 * (on open or on date-range change), so a full re-init is cheaper
 * than maintaining a diff.
 *
 * Supported `type` values:
 *  - `column` - vertical bars (day-of-week, hour-of-day)
 *  - `line`   - single-series line (monthly visits)
 *  - `dual`   - dual-axis line (spending + visit count)
 *  - `bar`    - horizontal bars (visits by employee)
 *  - `pie`    - donut / pie (top services share)
 */
@Component({
  selector: 'beautyapp-g2-chart',
  standalone: true,
  template: `<div #host class="g2-chart-host" style="width:100%;height:100%;min-height:240px;"></div>`,
})
export class G2ChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  type = input.required<'column' | 'line' | 'dual' | 'bar' | 'pie'>();
  data = input.required<any>();
  config = input<Record<string, unknown>>({});

  @ViewChild('host', { static: true }) host!: ElementRef<HTMLDivElement>;

  private plot: { render: () => void; destroy: () => void } | null = null;
  private ready = false;

  async ngAfterViewInit(): Promise<void> {
    this.ready = true;
    await this.render();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!this.ready) return;
    if (changes['data'] || changes['config'] || changes['type']) {
      await this.render();
    }
  }

  ngOnDestroy(): void {
    this.plot?.destroy();
    this.plot = null;
  }

  private async render(): Promise<void> {
    // Lazy import — keeps g2plot out of the main bundle.
    const g2 = await import('@antv/g2plot');
    this.plot?.destroy();
    const data = this.data();
    const baseCfg = { data, ...(this.config() ?? {}) } as any;
    const el = this.host.nativeElement;
    switch (this.type()) {
      case 'column':
        this.plot = new g2.Column(el, baseCfg);
        break;
      case 'line':
        this.plot = new g2.Line(el, baseCfg);
        break;
      case 'dual':
        // DualAxes expects data to be an array-of-arrays per plot.
        this.plot = new g2.DualAxes(el, baseCfg);
        break;
      case 'bar':
        this.plot = new g2.Bar(el, baseCfg);
        break;
      case 'pie':
        this.plot = new g2.Pie(el, baseCfg);
        break;
    }
    this.plot?.render();
  }
}
